// /opt/isp_erp/frontend/src/pages/admin/clientes/components/FacturacionCliente.jsx
import React, { useState, useEffect } from 'react';
import axios from '../../../../api/axios';
import { format } from 'date-fns';
import { facturasApi } from '../../../../api/finanzas/facturas.api';
import './FacturacionCliente.css';

export default function FacturacionCliente({ customerId }) {
  const [facturas, setFacturas] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFacturas();
    fetchResumen();
  }, [customerId]);

  const fetchFacturas = async () => {
    try {
      const response = await facturasApi.listar({ customer_id: customerId, limit: 50 });
      setFacturas(response.data);
    } catch (error) {
      console.error('Error cargando facturas:', error);
    }
  };

  const fetchResumen = async () => {
    try {
      const response = await facturasApi.resumenCliente(customerId);
      setResumen(response.data);
    } catch (error) {
      console.error('Error cargando resumen:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // ============================================
  // FUNCIÓN SEGURA PARA FORMATEAR FECHAS
  // ============================================
  const formatFechaSegura = (fecha) => {
    if (!fecha) return null;
    
    try {
      let fechaObj;
      
      // Si es string, limpiar formato (quitar hora si viene)
      if (typeof fecha === 'string') {
        // Tomar solo la parte YYYY-MM-DD si viene con hora
        const fechaLimpia = fecha.split('T')[0].split(' ')[0];
        fechaObj = new Date(fechaLimpia);
      } else {
        fechaObj = new Date(fecha);
      }
      
      // Verificar si es fecha válida
      if (fechaObj instanceof Date && !isNaN(fechaObj)) {
        return format(fechaObj, 'dd/MM/yyyy');
      }
    } catch (e) {
      console.warn("⚠️ Error formateando fecha:", fecha, e);
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="facturacion-loading-container">
        <div className="facturacion-loading-spinner"></div>
        <p>Cargando información de facturación...</p>
      </div>
    );
  }

  return (
    <div className="facturacion-cliente">
      {/* Header */}
      <div className="facturacion-header">
        <div className="facturacion-header-left">
          <div className="facturacion-header-icon">💰</div>
          <div>
            <h2>Facturación del Cliente</h2>
            <p>Historial de facturas y resumen financiero</p>
          </div>
        </div>
      </div>

      {/* Resumen de facturación */}
      {resumen && (
        <div className="facturacion-resumen">
          <div className="resumen-card resumen-total">
            <div className="resumen-icon">📊</div>
            <div>
              <p className="resumen-label">Total Facturado</p>
              <p className="resumen-value">{formatCurrency(resumen.total_facturado)}</p>
            </div>
          </div>

          <div className="resumen-card resumen-pagado">
            <div className="resumen-icon">✅</div>
            <div>
              <p className="resumen-label">Total Pagado</p>
              <p className="resumen-value">{formatCurrency(resumen.total_pagado)}</p>
            </div>
          </div>

          <div className="resumen-card resumen-pendiente">
            <div className="resumen-icon">⏳</div>
            <div>
              <p className="resumen-label">Saldo Pendiente</p>
              <p className="resumen-value">{formatCurrency(resumen.saldo_pendiente)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Lista de facturas */}
      <div className="facturacion-facturas">
        <div className="facturacion-subheader">
          <h3>Historial de Facturas</h3>
          <span className="facturas-count">{facturas.length} facturas</span>
        </div>

        {facturas.length === 0 ? (
          <div className="facturacion-empty">
            <div className="empty-icon">📄</div>
            <p>No hay facturas para este cliente</p>
          </div>
        ) : (
          <div className="facturacion-table-container">
            <table className="facturacion-table">
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Fecha Emisión</th>
                  <th>Fecha Vencimiento</th>
                  <th>Total</th>
                  <th>Saldo Pendiente</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {facturas.map((factura) => (
                  <tr key={factura.id} className="factura-row">
                    <td className="factura-numero">
                      <span className="factura-id">#{factura.numero_factura}</span>
                    </td>
                    <td>
                      <span className="factura-fecha">
                        {formatFechaSegura(factura.fecha_emision)}
                      </span>
                    </td>
                    <td>
                      <span className="factura-fecha">
                        {formatFechaSegura(factura.fecha_vencimiento)}
                      </span>
                    </td>
                    <td className="factura-monto">
                      {formatCurrency(factura.total)}
                    </td>
                    <td className="factura-saldo">
                      {formatCurrency(factura.saldo_pendiente)}
                    </td>
                    <td>
                      <span className={`factura-estado estado-${factura.estado?.toLowerCase()}`}>
                        {factura.estado === 'PENDIENTE' && '⏳ '}
                        {factura.estado === 'PAGADA' && '✅ '}
                        {factura.estado === 'ANULADA' && '❌ '}
                        {factura.estado === 'VENCIDA' && '⚠️ '}
                        {factura.estado}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Información adicional - CORREGIDA */}
      {resumen && resumen.saldo_pendiente > 0 && (
        <div className="facturacion-aviso">
          <div className="aviso-icon">⚠️</div>
          <div className="aviso-contenido">
            <p className="aviso-titulo">Saldo pendiente detectado</p>
            <p className="aviso-texto">
              El cliente tiene un saldo pendiente de {formatCurrency(resumen.saldo_pendiente)}.
              {formatFechaSegura(resumen.ultima_factura) && (
                <> Última factura: {formatFechaSegura(resumen.ultima_factura)}</>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
